import type { LaunchGrant } from '../launch-types'

/** The learner's lab page: a lab activity under the grant its query names. */
export const labRoute = '/launch/labs/:activityId'

/** The lab page for the activity under the grant. */
export function labPath(activityId: string, grant: LaunchGrant): string {
  return `/launch/labs/${encodeURIComponent(activityId)}?${grantQuery(grant).toString()}`
}

/** The grant a lab page's query names, or `null` when it names none or several. */
export function grantFromQuery(search: URLSearchParams): LaunchGrant | null {
  const named = [
    ['enrollmentId', search.get('enrollmentId')],
    ['subscriptionId', search.get('subscriptionId')],
    ['classId', search.get('classId')],
  ].filter((pair): pair is [string, string] => pair[1] !== null && pair[1] !== '')
  if (named.length !== 1) {
    return null
  }
  const [field, id] = named[0]!
  if (field === 'enrollmentId') {
    return { kind: 'enrollment', enrollmentId: id }
  }
  return field === 'subscriptionId' ? { kind: 'subscription', subscriptionId: id } : { kind: 'class', classId: id }
}

function grantQuery(grant: LaunchGrant): URLSearchParams {
  switch (grant.kind) {
    case 'enrollment':
      return new URLSearchParams({ enrollmentId: grant.enrollmentId })
    case 'subscription':
      return new URLSearchParams({ subscriptionId: grant.subscriptionId })
    case 'class':
      return new URLSearchParams({ classId: grant.classId })
  }
}

/** The API's lab page for the activity under the grant. */
export function labApi(activityId: string, grant: LaunchGrant): string {
  const query = grantQuery(grant)
  query.set('activityId', activityId)
  return `/launch/v1/labs?${query.toString()}`
}

/** The API's system-check lab launches. */
export const systemChecksApi = '/launch/v1/labs/system-checks'

/** One of a lab instance's actions. */
export function labInstanceActionApi(labInstanceId: string, action: 'resume' | 'end' | 'refresh'): string {
  return `/launch/v1/lab-instances/${encodeURIComponent(labInstanceId)}/${action}`
}

/** The system-check launch's body: the lab, and the lab activity and grant that let the person launch it. */
export function systemCheckBody(labProfileId: number, activityId: string, grant: LaunchGrant): Readonly<Record<string, string | number>> {
  return { labProfileId, activityId, ...Object.fromEntries(grantQuery(grant)) }
}

/** What the lab button hands the lab page, in the history entry's state. */
export interface LabState {
  /** Where Back goes: the page the lab was opened from. */
  readonly back: string
}

/** The lab page's state, or `null` when it was opened some other way or the state names no path on this site. */
export function labStateOf(state: unknown): LabState | null {
  if (typeof state !== 'object' || state === null) {
    return null
  }
  const { back } = state as Partial<Record<keyof LabState, unknown>>
  return typeof back === 'string' && /^\/(?![/\\])/.test(back) ? { back } : null
}
